import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import fr from 'facet-rest';
import { FDCallToActionButton, FDSecondaryActionButton } from '@facet-wealth/design';
import ProfileImagePicker from 'Components/ComponentLibrary/Molecules/ProfileImagePicker';
import { navigateToPage } from 'Views/Page/Content/Templates';
import SimplePicker from 'Helpers/SimplePicker';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import useHomeContext from 'Context/HomeContext';

const useStyles = buildUseStyles('HouseholdNextAction', (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  hackButtonStyle: {
    '& .FD-MuiButtonBase-root': {
      backgroundColor: theme.palette.facet.orange,
      color: theme.palette.common.white,
    },
  },
  plannerAvatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    marginBottom: theme.spacing(1.5),
  },
  text: {
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
}));

const HouseholdNextAction = ({ resource }) => {
  const classes = useStyles();
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { hasLegacyDashboardImprovement } = useHomeContext();

  const primaryButtonLink = resource?.relationship(fr.relationships.primaryAction.name);
  const secondaryButtonLink = resource?.relationship(fr.relationships.secondaryAction.name);

  const primaryButtonText = primaryButtonLink?.meta('prompt');
  const secondaryButtonText = secondaryButtonLink?.meta('prompt');

  const handlePrimaryButtonClick = () => {
    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Household Next Action - Primary dashboard button clicked', {
      eventProperties: {
        prompt: primaryButtonText,
        link: primaryButtonLink.link('related').url(),
      },
    });
    navigateToPage(primaryButtonLink?.link('related').url(), false);
  };

  const handleSecondaryButtonClick = () => {
    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Household Next Action - Secondary dashboard button clicked', {
      eventProperties: {
        prompt: secondaryButtonText,
        link: secondaryButtonLink.link('related').url(),
      },
    });
    navigateToPage(secondaryButtonLink?.link('related').url(), false);
  };

  return (
    <div className={classNames(classes.root)}>
      <ProfileImagePicker
        className={classes.plannerAvatar}
        resource={resource}
      />
      <Typography
        className={classes.text}
        color={'black'}
        variant={'h6'}
      >
        {resource?.attr('title')}
      </Typography>
      <div className={classes.buttonContainer}>
        <SimplePicker
          resource={resource}
          relationship={fr.relationships.primaryAction.name}
          hasRelationship={() => (
            <div className={classNames({
              [classes.hackButtonStyle]: hasLegacyDashboardImprovement,
            })}
            >
              <FDCallToActionButton label={primaryButtonText} onClick={handlePrimaryButtonClick} />
            </div>
          )}
        />
        <SimplePicker
          resource={resource}
          relationship={fr.relationships.secondaryAction.name}
          hasRelationship={() => (
            <FDSecondaryActionButton label={secondaryButtonText} onClick={handleSecondaryButtonClick} />
          )}
        />
      </div>
    </div>
  );
};

HouseholdNextAction.propTypes = {
  resource: PropTypes.object.isRequired,
};

export default HouseholdNextAction;
